import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { format, startOfMonth, endOfMonth } from 'date-fns';

export function localDate(date = new Date()) { return format(date, 'yyyy-MM-dd'); }
export function isScheduleEventUpcoming(event, now = new Date()) {
  if (!event?.event_date) return false;
  const today = localDate(now);
  if (event.event_date !== today) return event.event_date > today;
  if (!event.event_time) return true;
  return event.event_time.slice(0, 5) > format(now, 'HH:mm');
}
export async function fetchSchedule(start, end) {
  const makeQuery = () => {
    let query = supabase.from('official_schedule').select('*').gte('event_date', start);
    if (end) query = query.lte('event_date', end);
    return query.order('event_date', { ascending: true }).order('event_time', { ascending: true }).limit(100);
  };
  let response = await makeQuery().is('deleted_at', null);
  if (response.error?.code === '42703' && response.error.message?.includes('deleted_at')) response = await makeQuery();
  if (response.error) throw response.error;
  return response.data || [];
}
export function useClubSchedule(month = null) {
  const [now, setNow] = useState(() => new Date());
  const start = month ? localDate(startOfMonth(month)) : localDate(now);
  const end = month ? localDate(endOfMonth(month)) : null;
  const [state, setState] = useState({ data: [], loading: true, error: false });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ data: [], loading: true, error: false });
    fetchSchedule(start, end).then(data => { if (active) setState({ data, loading: false, error: false }); })
      .catch(() => { if (active) setState({ data: [], loading: false, error: true }); });
    return () => { active = false; };
  }, [start, end, attempt]);
  useEffect(() => {
    if (month) return undefined;
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, [month]);
  const retry = useCallback(() => setAttempt(n => n + 1), []);
  const data = month ? state.data : state.data.filter(event => isScheduleEventUpcoming(event, now));
  return { ...state, data, retry };
}
export function useClubNotices() {
  const [state, setState] = useState({ data: [], loading: true, error: false });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ data: [], loading: true, error: false });
    const load = async () => {
      const { data, error } = await supabase.from('announcements').select('id,title,content,priority,is_pinned,created_at,expires_at')
        .eq('is_active', true).or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
        .order('priority', { ascending: false }).order('is_pinned', { ascending: false }).order('created_at', { ascending: false }).limit(3);
      if (error) throw error;
      return data || [];
    };
    load().then(data => { if (active) setState({ data, loading: false, error: false }); }).catch(() => { if (active) setState({ data: [], loading: false, error: true }); });
    return () => { active = false; };
  }, [attempt]);
  return { ...state, retry: () => setAttempt(n => n + 1) };
}
